import CloseIcon from "@mui/icons-material/Close";
import TranslateIcon from "@mui/icons-material/Translate";
import {
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import { useTranslation } from "react-i18next";
import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { APP_IMAGES } from "../../assets";
import { baseUrl } from "../../services";
import {
  createServiceCategory,
  updateServiceCategory,
} from "../../services/serviceCategoryApiFn";
import { capitalizeText, handleOpenSnackBar } from "../../utils";
import { translateText } from "../../utils/translationService";
import { serviceCategoryValidation } from "../../utils/Validation";
import AppLoader from "../AppLoader";
import GlobalButton from "../CustomButton";
import KeywordsTagInput from "../KeywordsTagInput";
import Label from "../Label";
import TextInput from "../TextInputs";
import UploadBox from "../UploadBox";
import DialogHeader from "./Header";
import CategoryIcon from "@mui/icons-material/Category";
import DialogBody from "./Body";
import DialogActionButtons from "./Footer";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(4, 2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(2),
  },
  "& .MuiPaper-root": {
    width: "500px",
    [theme.breakpoints.down("sm")]: {
      margin: 12,
    },
  },
  "& .MuiInputBase-multiline": {
    padding: "6px !important",
  },
}));

const initialInputValues = {
  image: null,
  name_en: "",
  name_ro: "",
  description_en: "",
  description_ro: "",
  metaTitle_en: "",
  metaTitle_ro: "",
  metaDescription_en: "",
  metaDescription_ro: "",
  metaKeywords_en: [],
  metaKeywords_ro: [],
};

let paramsData = {};

const AddEditServiceCategoryDialog = forwardRef(({ }, ref) => {
  const openImageFile = useRef(null);
  const [open, setOpen] = useState(false);
  const { t } = useTranslation(["catalogue", "common"]);
  const [isLoading, setIsLoading] = useState({
    save: false,
    image: false,
    translateToEnglish: false,
  });
  const [inputValues, setInputValues] = useState(initialInputValues);
  const [inputError, setInputError] = useState({});
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);


  useImperativeHandle(ref, () => ({
    openDialog(params) {
      setOpen(true);
      paramsData = params;
      if (params.type === "edit") {
        const data = params.data;
        setInputValues({
          image: data?.image || null,
          name_en: capitalizeText(data?.name?.en) || "",
          name_ro: capitalizeText(data?.name?.ro) || "",
          description_en: data?.description?.en || "",
          description_ro: data?.description?.ro || "",
          metaTitle_en: capitalizeText(data?.metaTitle?.en) || "",
          metaTitle_ro: capitalizeText(data?.metaTitle?.ro) || "",
          metaDescription_en: data?.metaDescription?.en || "",
          metaDescription_ro: data?.metaDescription?.ro || "",
          metaKeywords_en: data?.metaKeywords?.en || [],
          metaKeywords_ro: data?.metaKeywords?.ro || [],
        });
      }
    },
    closeDialog() {
      handleClose();
    },
  }));

  const handleClose = () => {
    setOpen(false);
    setInputValues(initialInputValues);
    setInputError({});
  };

  const handleUploadImage = async (event) => {
    try {
      setIsLoading((prev) => ({ ...prev, image: true }));
      const formData = new FormData();
      formData.append("image", event.target.files[0]);
      const response = await axios({
        method: "post",
        url: `${baseUrl}image/upload`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });

      setInputError((pre) => ({ ...pre, image: "" }));
      setInputValues((pre) => ({ ...pre, image: response?.data?.url }));
      handleOpenSnackBar("success", t("success.mediaUploadSuccess", { ns: "common" }));
    } catch (error) {
      handleOpenSnackBar("error", t("error.mediaUploadFail", { ns: "common" }));
    } finally {
      openImageFile.current.value = null;
      setIsLoading((prev) => ({ ...prev, image: false }));
    }
  };


  const handleAddEditCategory = () => {
    const isValid = serviceCategoryValidation(inputValues, setInputError);
    if (isValid) return handleOpenSnackBar("error", t("resolveTheIssues", { ns: "common" }));

    let payload = {
      image: inputValues.image,
      name: {
        en: inputValues.name_en,
        ro: inputValues.name_ro,
      },
      description: {
        en: inputValues.description_en,
        ro: inputValues.description_ro,
      },
      metaTitle: {
        en: inputValues.metaTitle_en,
        ro: inputValues.metaTitle_ro,
      },
      metaDescription: {
        en: inputValues.metaDescription_en,
        ro: inputValues.metaDescription_ro,
      },
      metaKeywords: {
        en: inputValues.metaKeywords_en,
        ro: inputValues.metaKeywords_ro,
      },
    };

    if (paramsData?.type === "add") {
      handleApiCall(createServiceCategory, payload, "");
    } else {
      handleApiCall(updateServiceCategory, payload, paramsData?.data?._id);
    }
  };

  const handleApiCall = async (apiFunction, payload, id) => {
    try {
      setIsLoading((prev) => ({ ...prev, save: true }));
      const response = await apiFunction(id, payload);
      if (response?.data?.status === "success") {
        handleOpenSnackBar("success", response?.data?.message);
        handleClose();
        paramsData?.getData(searchKeys.serviceName);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", t("error.somethingWentWrong", { ns: "common" }));
    } finally {
      setIsLoading((prev) => ({ ...prev, save: false }));
    }
  };

  const handleTranslateToEnglish = async (key1, key2) => {
    setIsLoading((prev) => ({ ...prev, translateToEnglish: true }));
    try {
      const [englishText] = await Promise.all([
        translateText(inputValues[key1]),
      ]);
      setInputValues((prev) => ({
        ...prev,
        [key2]: englishText,
      }));
      setInputError((prev) => ({
        ...prev,
        [key2]: "",
      }));
    } catch (error) {
      handleOpenSnackBar("error", t("error.errorWhileTranslating", { ns: "common" }));
    } finally {
      setIsLoading((prev) => ({ ...prev, translateToEnglish: false }));
    }
  };

  const handleChangeMetaKeywords = (name, newValue) => {
    setInputValues((prev) => ({ ...prev, [name]: newValue }));
    setInputError((prev) => ({ ...prev, [name]: "" }));
  };

  const handleChageInputs = (e) => {
    const { name, value } = e.target;
    setInputError((pre) => ({ ...pre, [name]: "" }));
    setInputValues((pre) => ({ ...pre, [name]: value }));
  };

  return (
    <>
      <AppLoader isVisible={isLoading.save} />
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
      >
        {/* <Stack direction={"row"} alignItems={"center"}>
          <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
            {paramsData.type === "add" ? t("addCategory") : t("editCategory")}
          </DialogTitle>
        </Stack>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton> */}


        <DialogHeader
          title={paramsData.type === "add" ? t("addServiceCategory") : t("editServiceCategory")}
          secondaryHeading={paramsData.type === "add" ? t("addServiceCategoryInstruction") : t("editServiceCategoryInstruction")}
          icon={
            <CategoryIcon />
          }
          onClose={handleClose}
        />
        <DialogBody>
          <Grid container spacing={2}>
            {/* Large Image */}
            <Grid item xs={12}>
              <UploadBox
                label={t("image", { ns: "common" })}
                note={t("largeImageSizeNote", { ns: "common" })}
                value={inputValues.image}
                error={inputError.image}
                loading={isLoading.image}
                placeholderImage={APP_IMAGES.uploadImage}
                inputRef={openImageFile}
                accept="image/png, image/jpeg"
                height={{ xs: 200, md: 130 }}
                previewSize={{ xs: 160, md: 180 }}
                onClick={() => openImageFile.current.click()}
                onChange={handleUploadImage}
                onRemove={() => setInputValues({ ...inputValues, image: null })}
              />
            </Grid>

            <Grid item xs={12}>
              <Label
                required
                labelText={t("categoryNameInRomanian")}
                htmlFor={"category-name-ro"}
              />
              <TextInput
                id={"category-name-ro"}
                placeholder={t("enterNameInRomanian")}
                textLength={60}
                type={"text"}
                required
                name={"name_ro"}
                value={inputValues.name_ro}
                onChange={handleChageInputs}
                inputError={inputError.name_ro}
                helperText={t(inputError.name_ro)}
                InputEndIcon={
                  <Typography variant="body2" color="text.grey">
                    {inputValues.name_ro?.length}/60
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Stack
                mb={"5px"}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Label
                  required
                  labelText={t("categoryNameInEnglish")}
                  htmlFor={"category-name-en"}
                />
                <Chip
                  label={t("translateToEnglish", { ns: "common" })}
                  size="small"
                  onClick={() =>
                    handleTranslateToEnglish("name_ro", "name_en")
                  }
                  icon={
                    isLoading.translateToEnglish ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <TranslateIcon />
                    )
                  }
                  disabled={isLoading.translateToEnglish}
                />
              </Stack>
              <TextInput
                id={"category-name-en"}
                placeholder={t("enterNameInEnglish")}
                textLength={60}
                type={"text"}
                required
                name={"name_en"}
                value={inputValues.name_en}
                onChange={handleChageInputs}
                inputError={inputError.name_en}
                helperText={t(inputError.name_en)}
                InputEndIcon={
                  <Typography variant="body2" color="text.grey">
                    {inputValues.name_en?.length}/60
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Label
                required
                labelText={t(
                  "categoryDescriptionInRomanian",
                )}
                htmlFor={`catrgory-description-ro`}
              />
              <TextInput
                id={`catrgory-description-ro`}
                required
                placeholder={t(
                  "enterDescriptionInRomanian",
                )}
                name={`description_ro`}
                textLength={255}
                value={inputValues.description_ro}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.description_ro)}
                helperText={t(inputError.description_ro)}
                multiline
                rows={3}
                InputEndIcon={
                  <Typography variant="body2" color="text.grey">
                    {inputValues.description_ro?.length}/255
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Stack
                mb={"5px"}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Label
                  required
                  labelText={t(
                    "categoryDescriptionInEnglish",
                  )}
                  htmlFor={`catrgory-description-en`}
                  labelProps={{ marginBottom: 0 }}
                />
                <Chip
                  label={t("translateToEnglish", { ns: "common" })}
                  size="small"
                  onClick={() =>
                    handleTranslateToEnglish("description_ro", "description_en")
                  }
                  icon={
                    isLoading.translateToEnglish ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <TranslateIcon />
                    )
                  }
                  disabled={isLoading.translateToEnglish}
                />
              </Stack>
              <TextInput
                id={`catrgory-description-en`}
                required
                placeholder={t("enterDescriptionInEnglish")}
                name={`description_en`}
                textLength={255}
                value={inputValues.description_en}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.description_en)}
                helperText={t(inputError.description_en)}
                multiline
                rows={3}
                InputEndIcon={
                  <Typography variant="body2" color="text.grey">
                    {inputValues.description_en?.length}/255
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight={600}>
                {t("categoryMetaInfo")}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Label
                required
                labelText={t("categoryMetaTitleInRomanian")}
                htmlFor={`catrgory-meta-title-ro`}
              />
              <TextInput
                id={`catrgory-meta-title-ro`}
                required
                placeholder={t("enterMetaTitleInRomanian")}
                name={`metaTitle_ro`}
                textLength={255}
                value={inputValues.metaTitle_ro}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.metaTitle_ro)}
                helperText={t(inputError.metaTitle_ro)}
                InputEndIcon={
                  <Typography variant="body2" color="text.grey">
                    {inputValues.metaTitle_ro?.length}/255
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Stack
                mb={"5px"}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Label
                  required
                  labelText={t(
                    "categoryMetaTitleInEnglish",
                  )}
                  htmlFor={`catrgory-meta-title-en`}
                  labelProps={{ marginBottom: 0 }}
                />
                <Chip
                  label={t("translateToEnglish", { ns: "common" })}
                  size="small"
                  onClick={() =>
                    handleTranslateToEnglish("metaTitle_ro", "metaTitle_en")
                  }
                  icon={
                    isLoading.translateToEnglish ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <TranslateIcon />
                    )
                  }
                  disabled={isLoading.translateToEnglish}
                />
              </Stack>
              <TextInput
                id={`catrgory-meta-title-en`}
                required
                placeholder={t("enterMetaTitleInEnglish")}
                name={`metaTitle_en`}
                textLength={255}
                value={inputValues.metaTitle_en}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.metaTitle_en)}
                helperText={t(inputError.metaTitle_en)}
                InputEndIcon={
                  <Typography variant="body2" color="text.grey">
                    {inputValues.metaTitle_en?.length}/255
                  </Typography>
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Label
                required
                labelText={t(
                  "categoryMetaDescriptionInRomanian",
                )}
                htmlFor={`catrgory-meta-description-ro`}
              />
              <TextInput
                id={`catrgory-meta-description-ro`}
                required
                placeholder={t(
                  "enterMetaDescriptionInRomanian",
                )}
                name={`metaDescription_ro`}
                textLength={255}
                value={inputValues.metaDescription_ro}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.metaDescription_ro)}
                helperText={t(inputError.metaDescription_ro)}
                multiline
                rows={3}
                InputEndIcon={
                  <Typography variant="body2" color="text.grey">
                    {inputValues.metaDescription_ro?.length}/255
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Stack
                mb={"5px"}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Label
                  required
                  labelText={t(
                    "categoryMetaDescriptionInEnglish",
                  )}
                  htmlFor={`catrgory-meta-description-en`}
                  labelProps={{ marginBottom: 0 }}
                />
                <Chip
                  label={t("translateToEnglish", { ns: "common" })}
                  size="small"
                  onClick={() =>
                    handleTranslateToEnglish(
                      "metaDescription_ro",
                      "metaDescription_en",
                    )
                  }
                  icon={
                    isLoading.translateToEnglish ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <TranslateIcon />
                    )
                  }
                  disabled={isLoading.translateToEnglish}
                />
              </Stack>
              <TextInput
                id={`catrgory-meta-description-en`}
                required
                placeholder={t(
                  "enterMetaDescriptionInEnglish",
                )}
                name={`metaDescription_en`}
                textLength={255}
                value={inputValues.metaDescription_en}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.metaDescription_en)}
                helperText={t(inputError.metaDescription_en)}
                multiline
                rows={3}
                InputEndIcon={
                  <Typography variant="body2" color="text.grey">
                    {inputValues.metaDescription_en?.length}/255
                  </Typography>
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Label
                required
                labelText={t("metaKeywordsInRomanian")}
                htmlFor={`catrgory-meta-keywords-ro`}
              />
              <KeywordsTagInput
                placeholder={t(
                  "enterMetaKeywordsInRomanian",
                )}
                value={inputValues.metaKeywords_ro}
                onChange={(newValue) =>
                  handleChangeMetaKeywords("metaKeywords_ro", newValue)
                }
                textFieldProps={{
                  error: Boolean(inputError.metaKeywords_ro),
                  helperText: t(inputError.metaKeywords_ro),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Label
                required
                labelText={t("metaKeywordsInEnglish")}
                htmlFor={`catrgory-meta-keywords-en`}
              />
              <KeywordsTagInput
                placeholder={t(
                  "enterMetaKeywordsInEnglish",
                )}
                value={inputValues.metaKeywords_en}
                onChange={(newValue) =>
                  handleChangeMetaKeywords("metaKeywords_en", newValue)
                }
                textFieldProps={{
                  error: Boolean(inputError.metaKeywords_en),
                  helperText: t(inputError.metaKeywords_en),
                }}
              />
            </Grid>
          </Grid>
        </DialogBody>
        <DialogActionButtons
          onConfirm={handleAddEditCategory}
          onCancel={handleClose}
          confirmText={"save"}
          cancelText={"cancel"}
          isConfirmBtnDisable={isLoading.save}
          showCancelButton={true}
        />
      </BootstrapDialog>
    </>
  );
});

export default AddEditServiceCategoryDialog;
